import React from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import logo from "../../../../assets/logo/3d-logo.png";
import moment from "moment";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Link } from "react-router-dom";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

const B = {
    primary: "#C0357A",
    primaryDark: "#9B2860",
    grad: "linear-gradient(135deg, #C0357A 0%, #9B2860 100%)",
    border: "#F0E6DF",
    alpha06: "rgba(192,53,122,0.06)",
    alpha12: "rgba(192,53,122,0.12)",
    text: "#1C1917",
    muted: "#78716C",
};

const SectionLabel = ({ children }) => (
    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: B.primary, textTransform: "uppercase", letterSpacing: "0.8px", mb: 0.75 }}>
        {children}
    </Typography>
);

const InfoLine = ({ label, value, bold }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: "4px" }}>
        <Typography sx={{ fontSize: "12px", color: B.muted }}>{label}</Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: bold ? 700 : 500, color: B.text, textAlign: "right", maxWidth: "55%" }}>{value}</Typography>
    </Box>
);

// Contact line with an optional leading icon (icons are only shown on the user-side invoice)
const ContactLine = ({ icon: Icon, showIcon, children }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: showIcon ? "8px" : 0 }}>
        {showIcon && <Icon sx={{ fontSize: "15px", color: B.primary, mt: "2px", flexShrink: 0 }} />}
        <Typography sx={{ fontSize: "12px", color: B.muted, wordBreak: "break-word" }}>{children}</Typography>
    </Box>
);

const UserInvoices = ({ invoiceDetails, showContactIcons = false }) => {
    const { customer, provider, invoices } = invoiceDetails;
    let allInvoices = [];
    if (invoices?.length > 0) {
        allInvoices = invoices[0].invoices_methods || [];
    }

    const isPaid = invoiceDetails?.payment_status === "Paid" || invoiceDetails?.payment_status === "Pre Paid";

    const invoiceIds = invoices?.length > 0
        ? invoices.flatMap(inv => (inv?.invoices_methods || []).map(m => m?.id).filter(Boolean)).join(", ")
        : "N/A";

    return (
        <Box sx={{ background: "#FDF8FB", minHeight: "100vh", p: { xs: 2, sm: 3 } }}>
            <Box sx={{
                maxWidth: "820px",
                margin: "0 auto",
                background: "#fff",
                borderRadius: "16px",
                overflow: "hidden",
                border: `1px solid ${B.border}`,
                boxShadow: "0 4px 24px rgba(192,53,122,0.08)",
            }}>
                {/* Top gradient bar */}
                <Box sx={{ height: "5px", background: B.grad }} />

                <Box sx={{ p: { xs: "20px 16px", sm: "28px 32px" } }}>
                    {/* Header row */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Link to="/user/bookings" style={{ display: "flex", alignItems: "center" }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: B.alpha12, display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: B.alpha06 },
                                }}>
                                    <ArrowBackIosIcon sx={{ fontSize: "14px", color: B.primary, ml: "4px" }} />
                                </Box>
                            </Link>
                            <Box>
                                <Typography sx={{ fontSize: "22px", fontWeight: 800, color: B.primary, letterSpacing: "2px" }}>INVOICE</Typography>
                                <Typography sx={{ fontSize: "11px", color: B.muted }}>
                                    {invoiceIds !== "N/A" ? `#${invoiceIds}` : ""}
                                </Typography>
                            </Box>
                        </Box>
                        <img src={logo} alt="3D Lifestyle" style={{ height: "36px", objectFit: "contain" }} />
                    </Box>

                    {/* Status + Date bar */}
                    <Box sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1,
                        p: "10px 16px", borderRadius: "10px", background: B.alpha06, border: `1px solid ${B.border}`, mb: 3,
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: "12px", color: B.muted }}>Date:</Typography>
                            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: B.text }}>
                                {moment(invoiceDetails?.date).format("MMM D, YYYY")}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: "12px", color: B.muted }}>Amount:</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: B.text }}>${invoiceDetails?.grand_total}</Typography>
                        </Box>
                        <Box sx={{
                            px: "12px", py: "4px", borderRadius: "20px",
                            background: isPaid ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.1)",
                            border: `1px solid ${isPaid ? "rgba(22,163,74,0.3)" : "rgba(220,38,38,0.3)"}`,
                        }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: isPaid ? "#16A34A" : "#DC2626" }}>
                                {invoiceDetails?.payment_status}
                            </Typography>
                        </Box>
                    </Box>

                    {/* To / From / Info */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ p: "14px 16px", borderRadius: "10px", border: `1px solid ${B.border}`, height: "100%" }}>
                                <SectionLabel>Bill To</SectionLabel>
                                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: B.text, mb: 0.5 }}>{customer?.name}</Typography>
                                {customer?.phone && <ContactLine icon={PhoneOutlinedIcon} showIcon={showContactIcons}>{customer?.phone}</ContactLine>}
                                {customer?.email && <ContactLine icon={EmailOutlinedIcon} showIcon={showContactIcons}>{customer?.email}</ContactLine>}
                                {customer?.address?.address && <ContactLine icon={LocationOnOutlinedIcon} showIcon={showContactIcons}>{customer?.address?.address}</ContactLine>}
                                {customer?.address?.postal_code && <Typography sx={{ fontSize: "12px", color: B.muted, pl: showContactIcons ? "23px" : 0 }}>{customer?.address?.postal_code}</Typography>}
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ p: "14px 16px", borderRadius: "10px", border: `1px solid ${B.border}`, height: "100%" }}>
                                <SectionLabel>From</SectionLabel>
                                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: B.text, mb: 0.5 }}>{provider?.name}</Typography>
                                {provider?.phone && <ContactLine icon={PhoneOutlinedIcon} showIcon={showContactIcons}>{provider?.phone}</ContactLine>}
                                {provider?.address?.address && <ContactLine icon={LocationOnOutlinedIcon} showIcon={showContactIcons}>{provider?.address?.address}</ContactLine>}
                                {provider?.address?.postal_code && <Typography sx={{ fontSize: "12px", color: B.muted, pl: showContactIcons ? "23px" : 0 }}>{provider?.address?.postal_code}</Typography>}
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box sx={{ p: "14px 16px", borderRadius: "10px", background: B.alpha06, border: `1px solid ${B.border}`, height: "100%" }}>
                                <SectionLabel>Invoice Info</SectionLabel>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography sx={{ fontSize: "12px", color: B.muted }}>Invoice ID</Typography>
                                        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: B.text }}>{invoiceIds}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography sx={{ fontSize: "12px", color: B.muted }}>Date</Typography>
                                        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: B.text }}>{moment(invoiceDetails?.date).format("MM/DD/YYYY")}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Services table */}
                    <Box sx={{ borderRadius: "10px", border: `1px solid ${B.border}`, overflow: "hidden", mb: 3 }}>
                        {/* Table header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "10px 16px", background: B.alpha06, borderBottom: `1px solid ${B.border}` }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: B.muted, textTransform: "uppercase", letterSpacing: "0.6px", flex: 3 }}>Service</Typography>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: B.muted, textTransform: "uppercase", letterSpacing: "0.6px", width: "50px", textAlign: "center" }}>Qty</Typography>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: B.muted, textTransform: "uppercase", letterSpacing: "0.6px", width: "80px", textAlign: "right" }}>Amount</Typography>
                        </Box>

                        {/* Rows */}
                        {invoiceDetails?.services?.map((item, index) => (
                            <Box key={index} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "10px 16px", borderBottom: index < invoiceDetails.services.length - 1 ? `1px solid ${B.border}` : "none", "&:hover": { background: "rgba(192,53,122,0.02)" } }}>
                                <Typography sx={{ fontSize: "13px", color: B.text, flex: 3 }}>{item?.name}</Typography>
                                <Typography sx={{ fontSize: "13px", color: B.muted, width: "50px", textAlign: "center" }}>1</Typography>
                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: B.text, width: "80px", textAlign: "right" }}>${item?.booking_cost}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Totals */}
                    <Box sx={{ ml: "auto", maxWidth: { xs: "100%", sm: "360px" } }}>
                        <Box sx={{ p: "14px 16px", borderRadius: "10px", border: `1px solid ${B.border}` }}>
                            <InfoLine label="Sub Total (CAD)" value={`$${invoiceDetails?.services_total_cost}`} />
                            <InfoLine label="Tax" value={`$${invoiceDetails?.tax}`} />
                            {parseFloat(invoiceDetails?.tip || 0) > 0 && (
                                <InfoLine label="Tip" value={`$${invoiceDetails?.tip}`} />
                            )}
                            {parseFloat(invoiceDetails?.discount) > 0 && (
                                <InfoLine label="Discount" value={`-$${invoiceDetails?.discount}`} />
                            )}
                            <Box sx={{ my: 1, borderTop: `2px solid ${B.border}` }} />
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: "4px" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: B.text }}>Grand Total</Typography>
                                <Typography sx={{ fontSize: "18px", fontWeight: 800, color: B.primary }}>${invoiceDetails?.grand_total}</Typography>
                            </Box>

                            {Object.entries(allInvoices)?.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Box sx={{ borderTop: `1px dashed ${B.border}`, pt: 1 }}>
                                        {Object.entries(allInvoices).map(([key, value], index) => (
                                            <InfoLine key={index} label={value?.payment_methods} value={`-$${value?.amount}`} />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Box sx={{ mt: 1, borderTop: `2px solid ${B.border}`, pt: 1 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "8px 12px", borderRadius: "8px", background: parseFloat(invoiceDetails?.due_amount) > 0 ? "rgba(220,38,38,0.06)" : "rgba(22,163,74,0.06)", border: `1px solid ${parseFloat(invoiceDetails?.due_amount) > 0 ? "rgba(220,38,38,0.2)" : "rgba(22,163,74,0.2)"}` }}>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: B.muted }}>Due Amount</Typography>
                                    <Typography sx={{ fontSize: "16px", fontWeight: 800, color: parseFloat(invoiceDetails?.due_amount) > 0 ? "#DC2626" : "#16A34A" }}>
                                        ${invoiceDetails?.due_amount}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${B.border}`, display: "flex", justifyContent: "center", gap: { xs: 2, sm: 4 }, flexWrap: "wrap" }}>
                        {[
                            provider?.website || "3dlifestyle.ca",
                            provider?.email || "care@3dlifestyle.ca",
                            provider?.phone || "(416) 850-0231",
                        ].filter(Boolean).map((item, i) => (
                            <Typography key={i} sx={{ fontSize: "12px", color: B.muted }}>{item}</Typography>
                        ))}
                    </Box>
                </Box>

                {/* Bottom gradient bar */}
                <Box sx={{ height: "3px", background: B.grad }} />
            </Box>
        </Box>
    );
};

export default UserInvoices;
